import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, TrendingUp, RefreshCw, AlertCircle } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: 'Pending', color: 'yellow', icon: Clock },
    approved: { label: 'Approved', color: 'green', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'red', icon: XCircle },
  };
  
  const config = statusConfig[status] || { label: status, color: 'gray', icon: Clock };
  const Icon = config.icon;
  
  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    gray: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClasses[config.color]}`}>
      <Icon size={12} className="mr-1" />
      {config.label}
    </span>
  );
};

export default function LoanApprovalsPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('write-offs');
  const [selectedItem, setSelectedItem] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');

  // Fetch pending approvals
  const { data: writeOffs, isLoading: writeOffsLoading, error: writeOffsError } = useQuery({
    queryKey: ['loan-write-offs', 'pending'],
    queryFn: async () => {
      const response = await api.get('/loan-write-offs?status=pending');
      // Handle both paginated and non-paginated responses
      const responseData = response.data.data || response.data;
      return Array.isArray(responseData) ? responseData : (responseData.data || []);
    },
    enabled: can(PERMISSIONS.APPROVE_LOAN),
    retry: false, // Don't retry on error
  });

  const { data: restructures, isLoading: restructuresLoading, error: restructuresError } = useQuery({
    queryKey: ['loan-restructures', 'pending'],
    queryFn: async () => {
      const response = await api.get('/loan-restructures?status=pending');
      const responseData = response.data.data || response.data;
      return Array.isArray(responseData) ? responseData : (responseData.data || []);
    },
    enabled: can(PERMISSIONS.APPROVE_LOAN),
    retry: false,
  });

  const { data: refinances, isLoading: refinancesLoading, error: refinancesError } = useQuery({
    queryKey: ['loan-refinances', 'pending'],
    queryFn: async () => {
      const response = await api.get('/loan-refinances?status=pending');
      const responseData = response.data.data || response.data;
      return Array.isArray(responseData) ? responseData : (responseData.data || []);
    },
    enabled: can(PERMISSIONS.APPROVE_LOAN),
    retry: false,
  });

  // Approval mutations
  const approveWriteOff = useMutation({
    mutationFn: async ({ id, notes }) => {
      return await api.post(`/loan-write-offs/${id}/approve`, { notes });
    },
    onSuccess: () => {
      toast.success('Write-off approved successfully');
      queryClient.invalidateQueries(['loan-write-offs']);
      setSelectedItem(null);
      setApprovalNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to approve write-off');
    },
  });

  const rejectWriteOff = useMutation({
    mutationFn: async ({ id, notes }) => {
      return await api.post(`/loan-write-offs/${id}/reject`, { notes });
    },
    onSuccess: () => {
      toast.success('Write-off rejected');
      queryClient.invalidateQueries(['loan-write-offs']);
      setSelectedItem(null);
      setApprovalNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reject write-off');
    },
  });

  const approveRestructure = useMutation({
    mutationFn: async ({ id, notes }) => {
      return await api.post(`/loan-restructures/${id}/approve`, { notes });
    },
    onSuccess: () => {
      toast.success('Restructure approved successfully');
      queryClient.invalidateQueries(['loan-restructures']);
      setSelectedItem(null);
      setApprovalNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to approve restructure');
    },
  });

  const rejectRestructure = useMutation({
    mutationFn: async ({ id, notes }) => {
      return await api.post(`/loan-restructures/${id}/reject`, { notes });
    },
    onSuccess: () => {
      toast.success('Restructure rejected');
      queryClient.invalidateQueries(['loan-restructures']);
      setSelectedItem(null);
      setApprovalNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reject restructure');
    },
  });

  const approveRefinance = useMutation({
    mutationFn: async ({ id, notes }) => {
      return await api.put(`/loan-refinances/${id}/approve`, { notes });
    },
    onSuccess: () => {
      toast.success('Refinance approved successfully');
      queryClient.invalidateQueries(['loan-refinances']);
      setSelectedItem(null);
      setApprovalNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to approve refinance');
    },
  });

  const rejectRefinance = useMutation({
    mutationFn: async ({ id, notes }) => {
      return await api.put(`/loan-refinances/${id}/reject`, { notes });
    },
    onSuccess: () => {
      toast.success('Refinance rejected');
      queryClient.invalidateQueries(['loan-refinances']);
      setSelectedItem(null);
      setApprovalNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reject refinance');
    },
  });

  // Check permissions AFTER all hooks
  if (!can(PERMISSIONS.APPROVE_LOAN)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const handleApprove = () => {
    if (!selectedItem) return;

    const { type, id } = selectedItem;
    const notes = approvalNotes;

    if (type === 'write-off') {
      approveWriteOff.mutate({ id, notes });
    } else if (type === 'restructure') {
      approveRestructure.mutate({ id, notes });
    } else if (type === 'refinance') {
      approveRefinance.mutate({ id, notes });
    }
  };

  const handleReject = () => {
    if (!selectedItem) return;

    const { type, id } = selectedItem;
    const notes = approvalNotes;

    if (type === 'write-off') {
      rejectWriteOff.mutate({ id, notes });
    } else if (type === 'restructure') {
      rejectRestructure.mutate({ id, notes });
    } else if (type === 'refinance') {
      rejectRefinance.mutate({ id, notes });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const tabs = [
    { id: 'write-offs', label: 'Write-Offs', icon: XCircle, count: writeOffs?.length || 0 },
    { id: 'restructures', label: 'Restructures', icon: RefreshCw, count: restructures?.length || 0 },
    { id: 'refinances', label: 'Refinances', icon: TrendingUp, count: refinances?.length || 0 },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Loan Approvals</h1>
        <p className="text-gray-600 mt-1">Review and approve pending loan actions</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={18} className="mr-2" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-2 bg-blue-100 text-blue-600 py-0.5 px-2 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow">
        {/* Error Message */}
        {(writeOffsError || restructuresError || refinancesError) && (
          <div className="p-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-900">Database Not Ready</p>
                  <p className="text-sm text-yellow-800 mt-1">
                    The loan approval tables haven't been created yet. Please run the migrations:
                  </p>
                  <code className="block mt-2 p-2 bg-yellow-100 rounded text-xs">
                    php artisan migrate
                  </code>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'write-offs' && (
          <WriteOffsList
            items={writeOffs || []}
            loading={writeOffsLoading}
            onSelect={(item) => setSelectedItem({ type: 'write-off', ...item })}
            formatCurrency={formatCurrency}
          />
        )}
        {activeTab === 'restructures' && (
          <RestructuresList
            items={restructures || []}
            loading={restructuresLoading}
            onSelect={(item) => setSelectedItem({ type: 'restructure', ...item })}
            formatCurrency={formatCurrency}
          />
        )}
        {activeTab === 'refinances' && (
          <RefinancesList
            items={refinances || []}
            loading={refinancesLoading}
            onSelect={(item) => setSelectedItem({ type: 'refinance', ...item })}
            formatCurrency={formatCurrency}
          />
        )}
      </div>

      {/* Approval Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">
              {selectedItem.type === 'write-off' && 'Approve Write-Off'}
              {selectedItem.type === 'restructure' && 'Approve Restructure'}
              {selectedItem.type === 'refinance' && 'Approve Refinance'}
            </h3>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Details</h4>
                {selectedItem.type === 'write-off' && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Loan:</span>
                      <span className="font-medium">{selectedItem.loan?.loan_no}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Member:</span>
                      <span className="font-medium">{selectedItem.loan?.member?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Write-off Amount:</span>
                      <span className="font-medium text-red-600">{formatCurrency(selectedItem.write_off_amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reason:</span>
                      <span className="font-medium">{selectedItem.reason}</span>
                    </div>
                  </div>
                )}
                {selectedItem.type === 'restructure' && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Loan:</span>
                      <span className="font-medium">{selectedItem.loan?.loan_no}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Member:</span>
                      <span className="font-medium">{selectedItem.loan?.member?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium capitalize">{selectedItem.restructure_type?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reason:</span>
                      <span className="font-medium">{selectedItem.reason}</span>
                    </div>
                  </div>
                )}
                {selectedItem.type === 'refinance' && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Member:</span>
                      <span className="font-medium">{selectedItem.member?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium capitalize">{selectedItem.refinance_type?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">New Interest Rate:</span>
                      <span className="font-medium">{selectedItem.new_interest_rate}%</span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Approval Notes
                </label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="4"
                  placeholder="Add notes for this approval/rejection..."
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setSelectedItem(null);
                    setApprovalNotes('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WriteOffsList({ items, loading, onSelect, formatCurrency }) {
  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">Loading...</p>
      </div>
    );
  }

  // Ensure items is an array
  const itemsArray = Array.isArray(items) ? items : [];

  if (itemsArray.length === 0) {
    return (
      <div className="p-6 text-center">
        <XCircle size={48} className="mx-auto text-gray-400 mb-2" />
        <p className="text-gray-600">No pending write-offs</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {itemsArray.map((item) => (
        <div key={item.id} className="p-6 hover:bg-gray-50 cursor-pointer" onClick={() => onSelect(item)}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold">{item.loan?.loan_no}</h3>
                <StatusBadge status={item.status} />
              </div>
              <p className="text-sm text-gray-600 mt-1">{item.loan?.member?.name}</p>
              <div className="mt-2 flex items-center space-x-4 text-sm">
                <span className="text-gray-600">
                  Amount: <span className="font-medium text-red-600">{formatCurrency(item.write_off_amount)}</span>
                </span>
                <span className="text-gray-600">
                  Type: <span className="font-medium capitalize">{item.write_off_type}</span>
                </span>
                <span className="text-gray-600">
                  Reason: <span className="font-medium">{item.reason}</span>
                </span>
              </div>
            </div>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Review
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function RestructuresList({ items, loading, onSelect, formatCurrency }) {
  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">Loading...</p>
      </div>
    );
  }

  // Ensure items is an array
  const itemsArray = Array.isArray(items) ? items : [];

  if (itemsArray.length === 0) {
    return (
      <div className="p-6 text-center">
        <RefreshCw size={48} className="mx-auto text-gray-400 mb-2" />
        <p className="text-gray-600">No pending restructures</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {itemsArray.map((item) => (
        <div key={item.id} className="p-6 hover:bg-gray-50 cursor-pointer" onClick={() => onSelect(item)}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold">{item.loan?.loan_no}</h3>
                <StatusBadge status={item.status} />
              </div>
              <p className="text-sm text-gray-600 mt-1">{item.loan?.member?.name}</p>
              <div className="mt-2 flex items-center space-x-4 text-sm">
                <span className="text-gray-600">
                  Type: <span className="font-medium capitalize">{item.restructure_type?.replace('_', ' ')}</span>
                </span>
                {item.new_interest_rate && (
                  <span className="text-gray-600">
                    New Rate: <span className="font-medium">{item.new_interest_rate}%</span>
                  </span>
                )}
                {item.new_duration && (
                  <span className="text-gray-600">
                    New Duration: <span className="font-medium">{item.new_duration} months</span>
                  </span>
                )}
              </div>
            </div>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Review
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function RefinancesList({ items, loading, onSelect, formatCurrency }) {
  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">Loading...</p>
      </div>
    );
  }

  // Ensure items is an array
  const itemsArray = Array.isArray(items) ? items : [];

  if (itemsArray.length === 0) {
    return (
      <div className="p-6 text-center">
        <TrendingUp size={48} className="mx-auto text-gray-400 mb-2" />
        <p className="text-gray-600">No pending refinances</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {itemsArray.map((item) => (
        <div key={item.id} className="p-6 hover:bg-gray-50 cursor-pointer" onClick={() => onSelect(item)}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold">{item.member?.name}</h3>
                <StatusBadge status={item.status} />
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {item.old_loans?.length || 0} loan(s) to refinance
              </p>
              <div className="mt-2 flex items-center space-x-4 text-sm">
                <span className="text-gray-600">
                  Type: <span className="font-medium capitalize">{item.refinance_type?.replace('_', ' ')}</span>
                </span>
                <span className="text-gray-600">
                  New Rate: <span className="font-medium">{item.new_interest_rate}%</span>
                </span>
                <span className="text-gray-600">
                  Duration: <span className="font-medium">{item.new_duration} months</span>
                </span>
              </div>
            </div>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Review
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
